import {
  KratosIdentityDto,
  KratosSessionDto,
  MemberDto,
  PermissionDto,
} from '@careapp-group/ng-sdk';
import { CAAction, MemberBadgesActions } from '../actions';
import { Members } from '../actions/index';
import { DataListState } from '../data-list.state';
import { ReducerStateFactory } from '../state-factory.reducer';

export interface MembersStateInterface extends DataListState<MemberDto> {
  memberIdentities: { [id: string]: KratosIdentityDto };
  memberSessions: { [id: string]: KratosSessionDto[] };
  loadingIdentities: { [id: string]: boolean };
  loadingSessions: { [id: string]: boolean };
}
class MembersState extends ReducerStateFactory<MemberDto> {
  constructor() {
    super(Members);
  }

  public reducer(
    state: MembersStateInterface = {
      ...this.initialState,
      memberIdentities: {},
      memberSessions: {},
      loadingIdentities: {},
      loadingSessions: {},
    },
    action: CAAction
  ) {
    switch (action.type) {
      case Members.REQUEST_UPDATE_ROLE:
      case Members.REQUEST_PERMISSIONS_ADMIN:
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: true,
          },
        };
      case MemberBadgesActions.ResponseSuccessAddBadgeToPermission.type:
      case MemberBadgesActions.ResponseSuccessRemoveBadgeFromPermission.type:
      case Members.RESPONSE_SUCCESS_UPDATE_ROLE: {
        const newPermission: PermissionDto = action.payload;
        const member = { ...state.entities[newPermission.memberId] };
        member.permissions = [
          ...(member.permissions ?? []).filter((p) => p.id != newPermission.id),
          newPermission,
        ].sort((a: PermissionDto, b: PermissionDto) => {
          if (a.id < b.id) {
            return 1;
          } else if (a.id > b.id) {
            return -1;
          }
          return 0;
        });
        const entities = { ...state.entities, [member.id]: member };
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [member.id]: false },
          entities,
        };
      }
      case Members.RESPONSE_SUCCESS_PERMISSIONS_ADMIN: {
        const member = { ...state.entities[action.payload.id] };
        member.permissions = action.payload.permissions;
        const entities = { ...state.entities, [member.id]: member };
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [member.id]: false },
          entities,
        };
      }

      case Members.RESPONSE_FAIL_UPDATE_ROLE: {
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
        };
      }

      case Members.RESPONSE_FAIL_PERMISSIONS_ADMIN: {
        return {
          ...state,
          error: action.payload,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
        };
      }

      case Members.RequestMemberIdentity.type:
      case Members.RequestDeleteCredential.type: {
        return {
          ...state,
          loadingIdentities: { ...state.loadingIdentities, [(<any>action).memberId]: true },
        };
      }
      case Members.ResponseSuccessMemberIdentity.type:
      case Members.SuccessDeleteCredential.type: {
        return {
          ...state,
          loadingIdentities: { ...state.loadingIdentities, [(<any>action).memberId]: false },
          memberIdentities: {
            ...state.memberIdentities,
            [(<any>action).memberId]: (<any>action).identity,
          },
        };
      }
      case Members.ResponseFailMemberIdentity.type: {
        return {
          ...state,
          loadingIdentities: { ...state.loadingIdentities, [(<any>action).memberId]: false },
        };
      }

      case Members.RequestMemberSessions.type: {
        return {
          ...state,
          loadingSessions: { ...state.loadingSessions, [(<any>action).memberId]: true },
        };
      }

      case Members.ResponseSuccessMemberSessions.type: {
        return {
          ...state,
          loadingSessions: { ...state.loadingSessions, [(<any>action).memberId]: false },
          memberSessions: {
            ...state.memberSessions,
            [(<any>action).memberId]: (<any>action).sessions,
          },
        };
      }
      case Members.ResponseFailMemberSessions.type: {
        return {
          ...state,
          loadingSessions: { ...state.loadingSessions, [(<any>action).memberId]: false },
        };
      }
      case Members.RequestUpdatePermission.type: {
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [(<any>action).permissionId]: true,
          },
        };
      }

      case Members.ResponseSuccessUpdatePermission.type: {
        // Flow changes from customer-team permission updates to the members,
        // if that member exists in the store.
        const newPermission = (<any>action).permission;
        const member = { ...state.entities[newPermission.memberId] };
        if (!!member) {
          member.permissions = [
            ...(member.permissions ?? []).filter((p) => p.id != newPermission.id),
            newPermission,
          ];
          const entities = { ...state.entities, [member.id]: member };
          return {
            ...state,
            loadingEntities: { ...state.loadingEntities, [member.id]: false },
            entities,
          };
        }
        return state;
      }
      case Members.ResponseFailUpdatePermission.type: {
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [(<any>action).permissionId]: false,
          },
        };
      }

      case Members.RequestConvertToTenantAdmin.type: {
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [(<any>action).memberId]: true,
          },
        };
      }

      case Members.ResponseSuccessConvertToTenantAdmin.type: {
        const member = { ...state.entities[(<any>action).permission.memberId] };
        member.permissions = [(<any>action).permission];
        const entities = { ...state.entities, [member.id]: member };
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [member.id]: false },
          entities,
        };
      }
      case Members.ResponseFailConvertToTenantAdmin.type: {
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [(<any>action).memberId]: false,
          },
        };
      }
    }
    return super.reducer(state, action);
  }
}

export const state = new MembersState();

export function reducer(...args) {
  return state.reducer.apply(state, args);
}
